import React, { useEffect, useState, useMemo } from 'react';
import { useApi } from '../services/api';
import { Task, TaskTemplate } from '../types';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../components/ui/Button';
import { Tab } from '@headlessui/react';
import { PlusIcon } from '@heroicons/react/24/outline';

interface TaskCreateResponse {
  id: string;
  title: string;
  status: string;
  candidateId: string;
  candidateEmail: string;
  createdAt: string;
  message: string;
  error?: string;
}

const OrgAdminTasks: React.FC = () => {
  const { getTemplates, getTasks, createTask, inviteCandidate } = useApi();
  const [selectedTab, setSelectedTab] = useState(0);
  const [taskTemplates, setTaskTemplates] = useState<TaskTemplate[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const memoizedGetTemplates = useMemo(() => getTemplates, [getTemplates]);
  const memoizedGetTasks = useMemo(() => getTasks, [getTasks]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        if (selectedTab === 0) {
          const response = await memoizedGetTasks();
          setTasks(response.tasks || []);
        } else {
          const response = await memoizedGetTemplates();
          if (response.tasks) {
            setTaskTemplates(response.tasks as unknown as TaskTemplate[]);
          }
        }
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data.');
        selectedTab === 0 ? setTasks([]) : setTaskTemplates([]);
      }
      setLoading(false);
    };

    fetchData();
  }, [memoizedGetTemplates, memoizedGetTasks, selectedTab]);

  const handleCreateTask = async (templateId: string) => {
    try {
      const email = prompt('Enter candidate email:');
      if (!email) return;

      // First try to create the task
      const taskResponse = await createTask({
        templateId,
        candidateEmail: email
      }) as TaskCreateResponse;

      // If the candidate doesn't exist, send an invite
      if (taskResponse.error === 'CANDIDATE_NOT_FOUND') {
        await inviteCandidate({
          email,
          role: 'candidate',
          taskTemplateId: templateId
        });
        alert('Invitation sent to candidate');
      } else {
        alert('Task created successfully');
      }

      // Refresh tasks list
      const response = await memoizedGetTasks();
      setTasks(response.tasks || []);
    } catch (err) {
      console.error('Error creating task:', err);
      alert('Failed to create task or send invite');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="sm:flex sm:items-center sm:justify-between mb-4">
          <h1 className="text-2xl font-semibold text-gray-900">Task Management</h1>
          {selectedTab === 1 && (
            <Button
              variant="primary"
              onClick={() => navigate('/templates/create')}
              className="inline-flex items-center px-4 py-2"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Create New Template
            </Button>
          )}
        </div>

        <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
          <Tab.List className="flex space-x-1 rounded-xl bg-blue-900/20 p-1 mb-4">
            <Tab className={({ selected }) =>
              `w-full rounded-lg py-2.5 text-sm font-medium leading-5 
              ${selected 
                ? 'bg-white text-blue-700 shadow'
                : 'text-gray-600 hover:bg-white/[0.12] hover:text-gray-800'}`
            }>
              Tasks
            </Tab>
            <Tab className={({ selected }) =>
              `w-full rounded-lg py-2.5 text-sm font-medium leading-5 
              ${selected 
                ? 'bg-white text-blue-700 shadow'
                : 'text-gray-600 hover:bg-white/[0.12] hover:text-gray-800'}`
            }>
              Templates
            </Tab>
          </Tab.List>

          <Tab.Panels>
            <Tab.Panel>
              {/* Tasks Panel */}
              {loading ? (
                <p>Loading tasks...</p>
              ) : error ? (
                <p className="text-red-500">{error}</p>
              ) : tasks.length === 0 ? (
                <p>No tasks found.</p>
              ) : (
                <div className="bg-white shadow rounded-lg overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Candidate</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {tasks.map((task) => (
                        <tr key={task.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{task.title}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{task.candidateEmail}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{task.status}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <Link to={`/tasks/${task.id}`} className="text-indigo-600 hover:text-indigo-900">View</Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Tab.Panel>

            <Tab.Panel>
              {/* Templates Panel */}
              {loading ? (
                <p>Loading templates...</p>
              ) : error ? (
                <p className="text-red-500">{error}</p>
              ) : taskTemplates.length === 0 ? (
                <p>No task templates found.</p>
              ) : (
                <div className="bg-white shadow rounded-lg overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Difficulty</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {taskTemplates.map((template) => (
                        <tr key={template.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{template.title}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{template.status || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{template.difficulty || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                            <Link to={`/templates/${template.id}`} className="text-indigo-600 hover:text-indigo-900">View/Edit</Link>
                            <button
                              onClick={() => handleCreateTask(template.id)}
                              className="text-green-600 hover:text-green-900"
                            >
                              Assign
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
    </div>
  );
};

export default OrgAdminTasks; 